import { Check, CircleAlert, WifiOff, Wifi, CircleDashed, HelpCircle } from "lucide-react";

export function Dot({ color = "gray" }) {
  const map = {
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    gray: "bg-slate-400",
  };
  return <span className={`inline-block h-2 w-2 rounded-full ${map[color]}`} />;
}

// Indicador de estado de alta (completa / incompleta)
export function AltaBadge({ completa, testId }) {
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
        completa ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
      }`}
    >
      {completa ? <Check strokeWidth={2} className="h-3.5 w-3.5" /> : <CircleAlert strokeWidth={2} className="h-3.5 w-3.5" />}
      {completa ? "Alta completa" : "Alta incompleta"}
    </span>
  );
}

// Indicador técnico del sitio. UI-010/HEALTH-00x: el estado SIEMPRE refleja la evidencia; cero
// dispositivos o falta de telemetría nunca se muestran como "Todos en línea".
// `estado` viene del backend (`sitio.tecnico_estado`); `ok` se mantiene como respaldo compatible.
// Si no llega ninguno de los dos (p. ej. un DTO que no expone el estado técnico) NO se afirma nada:
// el fallback es "no confirmable": `ok === false` SIN estado no prueba una caída.
const claveTecnica = (estado, ok) => (
  estado && TECNICO[estado] ? estado : ok === true ? "ok" : "no_confirmable"
);
const TECNICO = {
  ok: { label: "Todos en línea", Icon: Wifi, iconCls: "text-emerald-500", cls: "bg-slate-50 text-slate-600" },
  degradado: { label: "Hay dispositivos fuera de línea", Icon: WifiOff, iconCls: "text-[#9B2C2C]", cls: "bg-slate-100 text-slate-700" },
  sin_dispositivos: { label: "Sin dispositivos", Icon: CircleDashed, iconCls: "text-slate-400", cls: "bg-slate-100 text-slate-500" },
  no_confirmable: { label: "Estado no confirmable", Icon: HelpCircle, iconCls: "text-indigo-500", cls: "bg-indigo-50 text-indigo-700" },
};

export function TecnicoBadge({ ok, estado, detalle, testId }) {
  const clave = claveTecnica(estado, ok);
  const { label, Icon, iconCls, cls } = TECNICO[clave];
  const d = detalle || {};
  const titulo = d.activos === undefined ? label
    : `${d.activos} activo(s) · ${d.en_linea} en línea · ${d.fuera_de_linea} fuera de línea · ${d.sin_telemetria} sin telemetría`;
  return (
    <span
      data-testid={testId}
      data-estado={clave}
      title={titulo}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${cls}`}
    >
      <Icon strokeWidth={1.75} className={`h-3.5 w-3.5 ${iconCls}`} />
      {label}
    </span>
  );
}

// UI-010: misma regla en línea de lista. Devuelve el ícono coherente con el estado técnico.
export function TecnicoIcono({ ok, estado, testId }) {
  const clave = claveTecnica(estado, ok);
  const { label, Icon, iconCls } = TECNICO[clave];
  return (
    <Icon data-testid={testId} data-estado={clave} aria-label={label} title={label}
      strokeWidth={1.5} className={`h-4 w-4 ${iconCls}`} />
  );
}

// Banner de advertencias (nunca bloquea)
export function WarningBanner({ items, title = "Alta incompleta — falta:", testId }) {
  if (!items || items.length === 0) return null;
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
    >
      <div className="flex items-center gap-2 text-amber-800 font-medium text-sm mb-2">
        <CircleAlert strokeWidth={2} className="h-4 w-4" /> {title}
      </div>
      <ul className="list-disc pl-6 space-y-1 text-sm text-amber-800">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  );
}
